// ============================================================================
// client/deco-papier.ts — LES DÉCORS ET LES TRÉSORS EN PAPIER DÉCOUPÉ
// ----------------------------------------------------------------------------
// Lot 5b. C'est la couche que les enfants posent dans l'éditeur, et c'est là
// qu'était le défaut le plus grave de l'audit du 28/07 : une carte de la famille
// portait SOIXANTE-CINQ cascades d'eau, bleu saturé, contour noir, visage
// souriant, dessinées PAR-DESSUS le jeu — plus visibles que le héros.
//
// En papier, la règle qui l'empêche de revenir est simple et tenable :
//   LE DÉCOR EST COLLÉ À PLAT, IL NE PORTE AUCUNE OMBRE PORTÉE.
// Comparé aux objets et aux personnages, qui en portent une franche, il est
// visiblement au fond — sans qu'on ait eu besoin de le rendre terne.
//
// LES TRÉSORS font exception, et c'est voulu : ce sont les seuls décors
// autorisés à briller, parce que leur raison d'être est d'attirer l'œil.
// ============================================================================

import { bruit, decoupe, papier, rgba } from './papier'

type Ctx = CanvasRenderingContext2D
const ENCRE = '#3a2c1e'
const PAP = '#f2e4cc'

/** Code runtime (shared/decor.ts) -> nom de forme. Les codes absents d'ici
 *  retombent sur l'ancien sprite : aucune carte ne peut se retrouver trouée. */
export const DECO_PAR_CODE: Record<number, string> = {
  1: 'torche', 2: 'plante', 3: 'banniere', 4: 'cristal', 5: 'tonneau', 6: 'crane',
  7: 'panneau', 8: 'chaine', 9: 'cactus', 10: 'bonhomme', 11: 'valve', 12: 'champignon',
  13: 'fleurs', 14: 'toile', 15: 'bougie', 16: 'herbe', 17: 'buisson', 18: 'rocher',
  19: 'souche', 20: 'lierre', 21: 'stalagmite', 22: 'pierres', 23: 'lanterne',
  24: 'fanion', 25: 'lucioles', 26: 'barriere', 27: 'fleche', 30: 'corail', 31: 'tombe',
  28: 'cascadeEau', 29: 'cascadeLave',
  84: 'hieroglyphes', 85: 'cartouche', 86: 'oeilHorus',
}

/** Les vingt trésors (codes 64+). */
export const TRESOR_PAR_CODE: Record<number, string> = {
  64: 'diamant', 65: 'rubis', 66: 'emeraude', 67: 'saphir', 68: 'amethyste',
  69: 'couronne', 70: 'sceptre', 71: 'calice', 72: 'anneau', 73: 'amulette',
  74: 'oeuf', 75: 'lingot', 76: 'sac', 77: 'idole', 78: 'masque', 79: 'scarabee',
  80: 'perle', 81: 'polaire', 82: 'rouage', 83: 'grimoire',
}

export function decoPapier(g: Ctx, x: number, y: number, S: number, kind: string): void {
  const c = { x: x + S / 2, y: y + S / 2 }
  /* AUCUNE ombre portée : c'est ce qui colle le décor au fond de la page pendant
     que le terrain et les personnages s'en décollent. La règle tient tout. */
  const boite = { x: x - S, y: y - S, w: S * 3, h: S * 3 }
  const D = (path: () => void, col: string, motif: string): void => decoupe(g, path, col, motif, boite, 0, 0, 0)
  const tige = (x0: number, y0: number, x1: number, y1: number, col: string, w: number): void => { g.strokeStyle = col; g.lineWidth = w; g.lineCap = 'round'
    g.beginPath(); g.moveTo(x0, y0); g.lineTo(x1, y1); g.stroke() }
  switch (kind) {
    case 'torche': D(() => { g.beginPath(); g.rect(c.x - 3, c.y - 2, 6, 20) }, '#8a6033', 'plis')
      D(() => { g.beginPath(); g.moveTo(c.x - 8, c.y - 2); g.quadraticCurveTo(c.x, c.y - 26, c.x + 8, c.y - 2); g.closePath() }, '#ff9a2e', 'marbre'); break
    case 'plante': for (let k = 0; k < 4; k++) D(() => { g.beginPath()
      g.ellipse(c.x - 10 + k * 7, c.y + 6 - (k % 2) * 8, 4.5, 9, (k - 1.5) * .4, 0, 7) }, '#5fa03f', 'plis'); break
    case 'banniere': D(() => { g.beginPath(); g.moveTo(c.x - 11, c.y - 20); g.lineTo(c.x + 11, c.y - 20)
      g.lineTo(c.x + 11, c.y + 12); g.lineTo(c.x, c.y + 4); g.lineTo(c.x - 11, c.y + 12); g.closePath() }, '#a8324a', 'chevrons'); break
    case 'cristal': D(() => { g.beginPath(); g.moveTo(c.x, c.y - 20); g.lineTo(c.x + 10, c.y + 2)
      g.lineTo(c.x, c.y + 18); g.lineTo(c.x - 10, c.y + 2); g.closePath() }, '#8fd0e8', 'guilloche'); break
    case 'tonneau': D(() => { g.beginPath(); g.ellipse(c.x, c.y + 2, 15, 20, 0, 0, 7) }, '#a06a34', 'quadrille')
      for (const dy of [-8, 2, 12]) tige(c.x - 13, c.y + dy, c.x + 13, c.y + dy, rgba(ENCRE, .34), 2); break
    case 'crane': D(() => { g.beginPath(); g.ellipse(c.x, c.y - 2, 13, 11, 0, 0, 7); g.rect(c.x - 7, c.y + 6, 14, 7) }, '#f0ead6', 'grain')
      g.fillStyle = rgba(ENCRE, .7); g.beginPath(); g.arc(c.x - 5, c.y - 2, 3.4, 0, 7); g.arc(c.x + 5, c.y - 2, 3.4, 0, 7); g.fill(); break
    case 'panneau': D(() => { g.beginPath(); g.rect(c.x - 15, c.y - 16, 30, 18) }, '#a06a34', 'plis')
      tige(c.x, c.y + 2, c.x, c.y + 20, '#8a6033', 4)
      g.fillStyle = rgba(ENCRE, .5); for (let k = 0; k < 3; k++) g.fillRect(c.x - 10, c.y - 12 + k * 5, 20 - k * 6, 2); break
    case 'chaine': for (let k = 0; k < 4; k++) { g.strokeStyle = '#8f9aa8'; g.lineWidth = 2.6
      g.beginPath(); g.ellipse(c.x, c.y - 20 + k * 11, 5, 6, 0, 0, 7); g.stroke() } break
    case 'cactus': D(() => { g.beginPath(); g.rect(c.x - 6, c.y - 18, 12, 36)
      g.rect(c.x - 16, c.y - 6, 10, 6); g.rect(c.x + 6, c.y - 12, 10, 6) }, '#4a9a4a', 'rayures'); break
    case 'bonhomme': D(() => { g.beginPath(); g.arc(c.x, c.y + 8, 12, 0, 7) }, '#f0f6fa', 'grain')
      D(() => { g.beginPath(); g.arc(c.x, c.y - 9, 8, 0, 7) }, '#f0f6fa', 'grain')
      g.fillStyle = ENCRE; g.beginPath(); g.arc(c.x - 3, c.y - 11, 1.6, 0, 7); g.arc(c.x + 3, c.y - 11, 1.6, 0, 7); g.fill()
      g.fillStyle = '#e07a2a'; g.beginPath(); g.moveTo(c.x, c.y - 8); g.lineTo(c.x + 9, c.y - 6); g.lineTo(c.x, c.y - 5); g.fill(); break
    case 'valve': D(() => { g.beginPath(); g.arc(c.x, c.y, 14, 0, 7) }, '#c07a34', 'quadrille')
      g.fillStyle = PAP; g.beginPath(); g.arc(c.x, c.y, 5, 0, 7); g.fill()
      for (let k = 0; k < 4; k++) { const a = k * Math.PI / 2 + .4
        tige(c.x + Math.cos(a) * 5, c.y + Math.sin(a) * 5, c.x + Math.cos(a) * 15, c.y + Math.sin(a) * 15, '#8a5a20', 3) } break
    case 'champignon': D(() => { g.beginPath(); g.rect(c.x - 4, c.y - 2, 8, 16) }, '#e8dcc4', 'grain')
      D(() => { g.beginPath(); g.ellipse(c.x, c.y - 2, 15, 11, 0, Math.PI, 0) }, '#c83a3a', 'pois'); break
    case 'fleurs': for (let k = 0; k < 3; k++) { const fx = c.x - 11 + k * 11
      tige(fx, c.y + 16, fx, c.y - 2, '#4a8a3a', 2.4)
      D(() => { g.beginPath(); g.arc(fx, c.y - 5, 6, 0, 7) }, (['#e86a8a', '#f0c93a', '#8fa8e8'][k] ?? '#e86a8a'), 'pois') } break
    case 'toile': g.strokeStyle = rgba('#e8e4d8', .8); g.lineWidth = 1.4
      for (let k = 0; k < 5; k++) { g.beginPath(); g.moveTo(x + 4, y + 4); g.lineTo(x + 4 + k * 12, y + S - 2); g.stroke() }
      for (let k = 1; k < 4; k++) { g.beginPath(); g.moveTo(x + 4, y + 4 + k * 12); g.lineTo(x + 4 + k * 12, y + 4); g.stroke() } break
    case 'bougie': D(() => { g.beginPath(); g.rect(c.x - 4, c.y - 4, 8, 20) }, '#f0e8d0', 'grain')
      D(() => { g.beginPath(); g.ellipse(c.x, c.y - 10, 4, 8, 0, 0, 7) }, '#ffc61f', 'marbre'); break
    case 'herbe': for (let k = 0; k < 7; k++) { const hx = c.x - 15 + k * 5
      const pen = (k % 3 - 1) * 6, haut = 10 + ((bruit(k, 4) * 9) | 0)
      g.strokeStyle = rgba(k % 2 ? '#5fa03f' : '#4a8a3a', .85); g.lineWidth = 1.8; g.lineCap = 'round'
      g.beginPath(); g.moveTo(hx, c.y + 18)
      g.quadraticCurveTo(hx + pen * .4, c.y + 18 - haut * .6, hx + pen, c.y + 18 - haut); g.stroke() } break
    case 'buisson': D(() => { g.beginPath(); g.arc(c.x - 8, c.y + 6, 10, 0, 7); g.arc(c.x + 8, c.y + 6, 10, 0, 7)
      g.arc(c.x, c.y - 2, 12, 0, 7) }, '#4a8a3a', 'pois'); break
    case 'rocher': D(() => { g.beginPath(); g.moveTo(c.x - 16, c.y + 16); g.lineTo(c.x - 10, c.y - 6)
      g.lineTo(c.x + 3, c.y - 12); g.lineTo(c.x + 15, c.y + 2); g.lineTo(c.x + 16, c.y + 16); g.closePath() }, '#8a8272', 'marbre'); break
    case 'souche': D(() => { g.beginPath(); g.rect(c.x - 13, c.y - 4, 26, 20) }, '#8a6033', 'quadrille')
      D(() => { g.beginPath(); g.ellipse(c.x, c.y - 4, 13, 5, 0, 0, 7) }, '#c09050', 'guilloche'); break
    case 'lierre': for (const dx of [-8, 6]) { g.strokeStyle = rgba('#5fa03f', .85); g.lineWidth = 2
      g.beginPath(); g.moveTo(c.x + dx, y); g.quadraticCurveTo(c.x + dx + 6, c.y, c.x + dx - 2, y + S); g.stroke() }
      for (let k = 0; k < 6; k++) D(() => { g.beginPath()
        g.ellipse(c.x + (k % 2 ? 9 : -11), y + 7 + k * 7, 5, 3.4, k % 2 ? -.5 : .5, 0, 7) }, '#4a8a3a', 'plis'); break
    case 'stalagmite': D(() => { g.beginPath(); g.moveTo(c.x - 11, c.y + 18); g.lineTo(c.x, c.y - 18)
      g.lineTo(c.x + 11, c.y + 18); g.closePath() }, '#9a90a8', 'marbre'); break
    case 'pierres': for (const p of [[-9, 12, 8], [7, 13, 7], [0, 2, 9]] as [number, number, number][])
      D(() => { g.beginPath(); g.arc(c.x + p[0], c.y + p[1], p[2], 0, 7) }, '#8a8272', 'marbre'); break
    case 'lanterne': tige(c.x, y + 2, c.x, c.y - 10, '#5a4a3a', 2.4)
      D(() => { g.beginPath(); g.rect(c.x - 9, c.y - 10, 18, 20) }, '#c8a03a', 'quadrille')
      g.fillStyle = 'rgba(255,230,140,.85)'; g.fillRect(c.x - 6, c.y - 7, 12, 14); break
    case 'fanion': tige(c.x - 11, y + 4, c.x - 11, y + S - 4, '#8a6033', 2.6)
      D(() => { g.beginPath(); g.moveTo(c.x - 10, c.y - 16); g.lineTo(c.x + 14, c.y - 8)
        g.lineTo(c.x - 10, c.y); g.closePath() }, '#c83a3a', 'rayures'); break
    case 'lucioles': for (let k = 0; k < 5; k++) { const lx = c.x - 14 + ((bruit(k, 3) * 30) | 0), ly = c.y - 14 + ((bruit(3, k) * 30) | 0)
      const gl = g.createRadialGradient(lx, ly, 0, lx, ly, 9); gl.addColorStop(0, 'rgba(255,240,160,.9)'); gl.addColorStop(1, 'rgba(255,220,80,0)')
      g.fillStyle = gl; g.beginPath(); g.arc(lx, ly, 9, 0, 7); g.fill() } break
    case 'barriere': for (const dx of [-11, 11]) D(() => { g.beginPath(); g.rect(c.x + dx - 3, c.y - 14, 6, 30) }, '#a06a34', 'plis')
      for (const dy of [-6, 6]) D(() => { g.beginPath(); g.rect(c.x - 16, c.y + dy, 32, 5) }, '#c08a4a', 'plis'); break
    case 'fleche': D(() => { g.beginPath(); g.moveTo(c.x - 16, c.y - 6); g.lineTo(c.x + 6, c.y - 6)
      g.lineTo(c.x + 6, c.y - 12); g.lineTo(c.x + 18, c.y); g.lineTo(c.x + 6, c.y + 12)
      g.lineTo(c.x + 6, c.y + 6); g.lineTo(c.x - 16, c.y + 6); g.closePath() }, '#a06a34', 'plis'); break
    /* ⚠️ Ici se trouvaient deux ÉBAUCHES de cascade (un simple fillRect translucide),
       écrites avant le rideau de papier plus bas. Elles arrivaient AVANT lui dans
       l'aiguillage, donc le vrai dessin n'a jamais été atteint : esbuild le disait
       (« duplicate-case ») et l'avertissement passait inaperçu dans la sortie de
       build. L'éditeur montrait un rectangle plat là où le jeu montre un rideau.
       Supprimées le 30/07. Une ébauche gardée « au cas où » finit par gagner. */
    case 'corail': g.strokeStyle = rgba('#e88870', .85); g.lineWidth = 3.4; g.lineCap = 'round'
      g.beginPath(); g.moveTo(c.x, c.y + 18); g.lineTo(c.x, c.y - 4); g.stroke()
      for (const br of [[-11, 4], [11, 2], [-6, 12], [8, 13]] as [number, number][]) { g.beginPath()
        g.moveTo(c.x, c.y - 4 + br[1]); g.lineTo(c.x + br[0], c.y - 12 + br[1]); g.stroke() } break
    case 'tombe': D(() => { g.beginPath(); g.moveTo(c.x - 12, c.y + 18); g.lineTo(c.x - 12, c.y - 6)
      g.quadraticCurveTo(c.x, c.y - 22, c.x + 12, c.y - 6); g.lineTo(c.x + 12, c.y + 18); g.closePath() }, '#9a92a0', 'marbre')
      g.fillStyle = rgba(ENCRE, .4); g.fillRect(c.x - 2, c.y - 10, 4, 14); g.fillRect(c.x - 7, c.y - 5, 14, 4); break

    /* ── LES DEUX CASCADES ─────────────────────────────────────────────────
       Ce sont ELLES qu'a nommées l'audit du 28/07 : « Gorges wecoulcowl » en
       porte SOIXANTE-CINQ, et l'ancien sprite les rendait plus visibles que le
       héros. Elles sont déjà plafonnées à 42 % par le rendu ; ici on leur donne
       la seule forme qui les remette au fond — un RIDEAU de bandes verticales,
       aucun contour, aucune écume. La cascade est une texture, pas un objet. */
    case 'cascadeEau': case 'cascadeLave': {
      const eau = kind === 'cascadeEau'
      const cols = eau ? ['#6fb0d8', '#8fc8e8', '#a8d8f0'] : ['#e8511e', '#ff7a2e', '#ffa04a']
      for (let k = 0; k < 5; k++) {
        const bx = x + 5 + k * (S - 12) / 4.6, w = S * (0.1 + (k % 2) * 0.035)
        const h0 = k % 2 ? 0 : S * 0.06
        D(() => { g.beginPath(); g.rect(bx, y + h0, w, S - h0) }, cols[k % 3] ?? cols[0]!, 'plis')
      }
      break
    }

    /* ── LES TROIS SIGNES ÉGYPTIENS (biome désert) ─────────────────────────
       Gravés, donc CREUSÉS : de l'encre en creux dans le papier, jamais des
       pièces posées dessus. C'est ce qui les distingue d'un objet à ramasser. */
    case 'hieroglyphes': {
      g.fillStyle = rgba(ENCRE, .42)
      for (let k = 0; k < 3; k++) { const gy = c.y - 14 + k * 12
        g.fillRect(c.x - 11, gy, 5, 8); g.fillRect(c.x - 3, gy + 2, 7, 3)
        g.beginPath(); g.arc(c.x + 8, gy + 4, 3.2, 0, 7); g.fill() }
      break
    }
    case 'cartouche': {
      D(() => { g.beginPath(); g.ellipse(c.x, c.y, 10, 19, 0, 0, 7) }, '#e0c48a', 'guilloche')
      g.strokeStyle = rgba(ENCRE, .5); g.lineWidth = 2
      g.beginPath(); g.ellipse(c.x, c.y, 10, 19, 0, 0, 7); g.stroke()
      g.fillStyle = rgba(ENCRE, .45)
      for (let k = 0; k < 3; k++) g.fillRect(c.x - 4, c.y - 11 + k * 9, 8, 4)
      break
    }
    case 'oeilHorus': {
      D(() => { g.beginPath(); g.ellipse(c.x - 1, c.y - 3, 13, 8, 0, 0, 7) }, '#f0ead6', 'grain')
      g.fillStyle = '#2f7fa8'; g.beginPath(); g.arc(c.x - 1, c.y - 3, 4.6, 0, 7); g.fill()
      g.fillStyle = ENCRE; g.beginPath(); g.arc(c.x - 1, c.y - 3, 2, 0, 7); g.fill()
      tige(c.x - 11, c.y + 3, c.x - 6, c.y + 14, ENCRE, 2.6)   // la larme
      tige(c.x + 8, c.y + 3, c.x + 13, c.y + 11, ENCRE, 2.6)   // le trait qui remonte
      break
    }
  }
}

export function tresorPapier(g: Ctx, x: number, y: number, S: number, kind: string): void {
  const c = { x: x + S / 2, y: y + S / 2 }
  /* les trésors sont les SEULS décors autorisés à briller : c'est leur fonction */
  const eclat = (r = 22): void => { const gl = g.createRadialGradient(c.x, c.y, 1, c.x, c.y, r)
    gl.addColorStop(0, 'rgba(255,248,200,.5)'); gl.addColorStop(1, 'rgba(255,230,120,0)')
    g.fillStyle = gl; g.beginPath(); g.arc(c.x, c.y, r, 0, 7); g.fill() }
  const boite = { x: x - S, y: y - S, w: S * 3, h: S * 3 }
  const D = (path: () => void, col: string, motif: string): void => decoupe(g, path, col, motif, boite, 2, 3, .2)
  const gemme = (col: string): void => { eclat(); D(() => { g.beginPath()
    g.moveTo(c.x, c.y - 16); g.lineTo(c.x + 13, c.y - 4); g.lineTo(c.x, c.y + 16); g.lineTo(c.x - 13, c.y - 4); g.closePath() }, col, 'guilloche')
    g.fillStyle = rgba('#ffffff', .5); g.beginPath(); g.moveTo(c.x, c.y - 14); g.lineTo(c.x + 6, c.y - 5); g.lineTo(c.x, c.y + 2); g.closePath(); g.fill() }
  switch (kind) {
    case 'diamant': gemme('#bfe8f5'); break
    case 'rubis': gemme('#d8324a'); break
    case 'emeraude': gemme('#2fa060'); break
    case 'saphir': gemme('#2f5ac8'); break
    case 'amethyste': gemme('#9040c8'); break
    case 'couronne': eclat(); D(() => { g.beginPath(); g.moveTo(c.x - 17, c.y + 10)
      for (let k = 0; k <= 4; k++) { g.lineTo(c.x - 17 + k * 8.5, c.y - 12); g.lineTo(c.x - 13 + k * 8.5, c.y + 2) }
      g.lineTo(c.x + 17, c.y + 10); g.closePath() }, '#ffc61f', 'guilloche')
      g.fillStyle = '#d8324a'; g.beginPath(); g.arc(c.x, c.y + 5, 3.4, 0, 7); g.fill(); break
    case 'sceptre': eclat(); D(() => { g.beginPath(); g.rect(c.x - 3, c.y - 6, 6, 26) }, '#c8a03a', 'quadrille')
      D(() => { g.beginPath(); g.arc(c.x, c.y - 12, 9, 0, 7) }, '#8fd0e8', 'guilloche'); break
    case 'calice': eclat(); D(() => { g.beginPath(); g.moveTo(c.x - 12, c.y - 14); g.lineTo(c.x + 12, c.y - 14)
      g.quadraticCurveTo(c.x, c.y + 8, c.x - 12, c.y - 14); g.closePath() }, '#ffc61f', 'guilloche')
      D(() => { g.beginPath(); g.rect(c.x - 3, c.y + 2, 6, 10); g.rect(c.x - 10, c.y + 12, 20, 5) }, '#c8a03a', 'plis'); break
    case 'anneau': eclat(); g.strokeStyle = '#ffc61f'; g.lineWidth = 6
      g.beginPath(); g.arc(c.x, c.y + 4, 12, 0, 7); g.stroke()
      D(() => { g.beginPath(); g.arc(c.x, c.y - 12, 6, 0, 7) }, '#d8324a', 'guilloche'); break
    case 'amulette': eclat(); g.strokeStyle = '#c8a03a'; g.lineWidth = 2.4
      g.beginPath(); g.arc(c.x, c.y - 6, 14, .5, 2.64); g.stroke()
      D(() => { g.beginPath(); g.ellipse(c.x, c.y + 8, 9, 11, 0, 0, 7) }, '#2fa060', 'guilloche'); break
    case 'oeuf': eclat(); D(() => { g.beginPath(); g.ellipse(c.x, c.y, 12, 17, 0, 0, 7) }, '#e8a0c8', 'guilloche')
      g.strokeStyle = '#ffc61f'; g.lineWidth = 2; g.beginPath(); g.moveTo(c.x - 11, c.y); g.lineTo(c.x + 11, c.y); g.stroke(); break
    case 'lingot': eclat(); D(() => { g.beginPath(); g.moveTo(c.x - 17, c.y + 9); g.lineTo(c.x - 12, c.y - 7)
      g.lineTo(c.x + 12, c.y - 7); g.lineTo(c.x + 17, c.y + 9); g.closePath() }, '#ffc61f', 'guilloche'); break
    case 'sac': eclat(); D(() => { g.beginPath(); g.ellipse(c.x, c.y + 5, 15, 14, 0, 0, 7) }, '#c8a878', 'pois')
      g.strokeStyle = '#8a6033'; g.lineWidth = 3; g.beginPath(); g.moveTo(c.x - 7, c.y - 8); g.lineTo(c.x + 7, c.y - 8); g.stroke()
      g.fillStyle = '#ffc61f'; g.beginPath(); g.arc(c.x - 4, c.y + 4, 4, 0, 7); g.arc(c.x + 5, c.y + 7, 4, 0, 7); g.fill(); break
    case 'idole': eclat(); D(() => { g.beginPath(); g.moveTo(c.x - 11, c.y + 17); g.lineTo(c.x - 8, c.y - 4)
      g.lineTo(c.x, c.y - 17); g.lineTo(c.x + 8, c.y - 4); g.lineTo(c.x + 11, c.y + 17); g.closePath() }, '#ffc61f', 'marbre')
      g.fillStyle = rgba(ENCRE, .5); g.beginPath(); g.arc(c.x - 4, c.y - 4, 2, 0, 7); g.arc(c.x + 4, c.y - 4, 2, 0, 7); g.fill(); break
    case 'masque': eclat(); D(() => { g.beginPath(); g.ellipse(c.x, c.y, 14, 18, 0, 0, 7) }, '#c8a03a', 'chevrons')
      g.fillStyle = ENCRE; g.beginPath(); g.ellipse(c.x - 5, c.y - 4, 3.4, 2.4, 0, 0, 7)
      g.ellipse(c.x + 5, c.y - 4, 3.4, 2.4, 0, 0, 7); g.fill()
      g.fillRect(c.x - 4, c.y + 7, 8, 2.4); break
    case 'scarabee': eclat(); D(() => { g.beginPath(); g.ellipse(c.x, c.y + 2, 13, 15, 0, 0, 7) }, '#2fa090', 'guilloche')
      g.strokeStyle = rgba(ENCRE, .5); g.lineWidth = 1.8; g.beginPath(); g.moveTo(c.x, c.y - 12); g.lineTo(c.x, c.y + 16); g.stroke()
      for (const s of [-1, 1]) for (let k = 0; k < 3; k++) { g.beginPath()
        g.moveTo(c.x + s * 11, c.y - 6 + k * 9); g.lineTo(c.x + s * 18, c.y - 10 + k * 9); g.stroke() } break
    case 'perle': eclat(); D(() => { g.beginPath(); g.ellipse(c.x, c.y + 8, 18, 9, 0, Math.PI, 0) }, '#e8b8c8', 'guilloche')
      D(() => { g.beginPath(); g.arc(c.x, c.y + 2, 8, 0, 7) }, '#f8f0f4', 'guilloche'); break
    case 'polaire': eclat(); for (let k = 0; k < 3; k++) { const a = k * Math.PI / 3
      D(() => { g.beginPath(); g.ellipse(c.x, c.y, 3.4, 18, a, 0, 7) }, '#bfe8f5', 'guilloche') } break
    case 'rouage': eclat(); D(() => { g.beginPath()
      for (let k = 0; k < 24; k++) { const a = k / 24 * Math.PI * 2, r = k % 2 ? 12 : 17
        k ? g.lineTo(c.x + Math.cos(a) * r, c.y + Math.sin(a) * r) : g.moveTo(c.x + Math.cos(a) * r, c.y + Math.sin(a) * r) }
      g.closePath() }, '#c07a34', 'quadrille')
      g.fillStyle = PAP; g.beginPath(); g.arc(c.x, c.y, 5, 0, 7); g.fill(); break
    case 'grimoire': eclat(); D(() => { g.beginPath(); g.rect(c.x - 14, c.y - 16, 28, 32) }, '#8a3a4a', 'chevrons')
      D(() => { g.beginPath(); g.rect(c.x - 10, c.y - 13, 20, 26) }, '#f0e8d0', 'grain')
      g.fillStyle = '#ffc61f'; g.beginPath(); g.arc(c.x, c.y, 5, 0, 7); g.fill(); break
  }
}

/* ----------------------------------------------------------------- le cache */
/* Les décos sont redessinées à CHAQUE image (elles ne sont pas dans le cache du
   terrain, à cause des overlays animés). Sans cache par forme, le papier serait
   ruineux ici : c'est le point le plus sensible des six lots. */
const CACHE = new Map<string, HTMLCanvasElement>()
const CACHE_MAX = 90
const MARGE = 0.5             // l'arbre, la chaîne et la bannière débordent de leur case

/** Peint une déco (ou un trésor) dans une case S×S au coin (x, y).
 *  Renvoie faux si le code est inconnu : l'appelant retombe alors sur l'ancien sprite. */
export function blitDeco(ctx: Ctx, code: number, S: number, x: number, y: number): boolean {
  const nom = DECO_PAR_CODE[code] ?? TRESOR_PAR_CODE[code]
  if (!nom) return false
  const T = Math.max(8, Math.round(S))
  const cle = nom + '|' + T
  let cv = CACHE.get(cle)
  if (!cv) {
    const m = Math.ceil(T * MARGE)
    cv = document.createElement('canvas')
    cv.width = T + m * 2; cv.height = T + m * 2
    const g = cv.getContext('2d')
    if (g) {
      if (DECO_PAR_CODE[code]) decoPapier(g, m, m, T, nom)
      else tresorPapier(g, m, m, T, nom)
    }
    if (CACHE.size >= CACHE_MAX) { const k = CACHE.keys().next().value; if (k) CACHE.delete(k) }
    CACHE.set(cle, cv)
  }
  const m = Math.ceil(T * MARGE)
  ctx.drawImage(cv, x - m, y - m)
  return true
}
